import { useEffect, useState } from "react";

interface PaymentCountdownProps {
  /** ISO date string of the next payment / period end. */
  target: string;
}

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  overdue: boolean;
}

function computeRemaining(target: string): Remaining {
  const diffMs = new Date(target).getTime() - Date.now();
  const overdue = diffMs <= 0;
  const abs = Math.abs(diffMs);
  return {
    days: Math.floor(abs / (1000 * 60 * 60 * 24)),
    hours: Math.floor((abs / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((abs / (1000 * 60)) % 60),
    seconds: Math.floor((abs / 1000) % 60),
    overdue,
  };
}

function Segment({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl sm:text-3xl font-bold tabular-nums tracking-tight">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[11px] uppercase tracking-wide text-slate-400">
        {label}
      </span>
    </div>
  );
}

export function PaymentCountdown({ target }: PaymentCountdownProps) {
  const [remaining, setRemaining] = useState<Remaining>(() =>
    computeRemaining(target),
  );

  useEffect(() => {
    setRemaining(computeRemaining(target));
    const id = setInterval(() => setRemaining(computeRemaining(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  return (
    <div>
      <div className="flex items-center gap-3 sm:gap-5">
        <Segment value={remaining.days} label="days" />
        <span className="text-2xl font-bold text-slate-300">:</span>
        <Segment value={remaining.hours} label="hrs" />
        <span className="text-2xl font-bold text-slate-300">:</span>
        <Segment value={remaining.minutes} label="min" />
        <span className="text-2xl font-bold text-slate-300">:</span>
        <Segment value={remaining.seconds} label="sec" />
      </div>
      {remaining.overdue && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-2 font-medium">
          Payment overdue
        </p>
      )}
    </div>
  );
}
